import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import pogImage from "../../../assets/casino/pog.png";
import rouletteLogo from "../../../assets/Roulette/RouletteLogo.png";
import CasinoNoticeModal from "./roulette/CasinoNoticeModal";
import RouletteBetModal from "./roulette/RouletteBetModal";
import RouletteWheel3D from "./roulette/RouletteWheel3D";
import useRouletteSfx from "./roulette/useRouletteSfx";
import {
  CasinoApiError,
  getActiveRouletteSpin,
  getCasinoProfile,
  getRouletteLatest,
  lockRouletteBets,
  settleRouletteSpin,
  startRouletteSpin,
} from "./roulette/rouletteApi";
import { formatPogs } from "./roulette/rouletteConstants";

const LOCAL_PREVIEW_HOSTS = new Set(["localhost", "127.0.0.1"]);
const DEV_PREVIEW_BALANCE = 50000;

function useArcadeWalletAutoScale(displayValue) {
  const frameRef = useRef(null);
  const valueRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const frame = frameRef.current;
    const value = valueRef.current;
    if (!frame || !value) return undefined;

    const measure = () => {
      const availableWidth = Math.max(1, frame.clientWidth);
      const naturalWidth = Math.max(1, value.scrollWidth);
      const nextScale = Math.min(1, availableWidth / naturalWidth);
      setScale((current) =>
        Math.abs(current - nextScale) < 0.002 ? current : nextScale
      );
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(frame);
      observer.observe(value);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", measure, { passive: true });
    return () => window.removeEventListener("resize", measure);
  }, [displayValue]);

  return { frameRef, valueRef, scale };
}

const croupierAssetContext = require.context(
  "../../../assets/casino",
  false,
  /^\.\/croupier\d*\.png$/
);

function getCroupierSortValue(assetKey) {
  const match = String(assetKey).match(/^\.\/croupier(\d*)\.png$/i);

  if (!match) {
    return Number.MAX_SAFE_INTEGER;
  }

  return match[1] ? Number(match[1]) : 1;
}

function normaliseAssetModule(assetModule) {
  return assetModule?.default || assetModule;
}

const CROUPIER_NAMES = {
  "./croupier.png": "Jack",
  "./croupier2.png": "Chantelle",
  "./croupier3.png": "Trina",
  "./croupier4.png": "Mae",
};

function getCroupierDisplayName(assetKey) {
  return CROUPIER_NAMES[assetKey] || "Unknown";
}

function getCroupierVoiceId(croupierName) {
  return String(croupierName || "")
    .trim()
    .toLowerCase();
}

const CROUPIER_IMAGES = croupierAssetContext
  .keys()
  .sort((first, second) => getCroupierSortValue(first) - getCroupierSortValue(second))
  .map((key) => ({
    key,
    name: getCroupierDisplayName(key),
    src: normaliseAssetModule(croupierAssetContext(key)),
  }));

const ROULETTE_PREVIEW_POCKETS = [
  "0",
  "00",
  ...Array.from({ length: 36 }, (_, index) => String(index + 1)),
];

function buildPreviewLatest() {
  return Array.from(
    { length: 8 },
    () =>
      ROULETTE_PREVIEW_POCKETS[
        Math.floor(Math.random() * ROULETTE_PREVIEW_POCKETS.length)
      ]
  );
}

function isLocalPreviewHost() {
  return LOCAL_PREVIEW_HOSTS.has(window.location.hostname);
}

function createPreviewSpin(draftBets, totalStake) {
  const createdAt = new Date().toISOString();
  const spinToken = Date.now().toString(36).toUpperCase();

  return {
    spinId: `preview_${spinToken}`,
    discordId: "LOCAL_PREVIEW",
    game: "roulette",
    status: "LOCKED",
    bets: draftBets.map((bet, index) => ({
      betId: `preview_bet_${String(index + 1).padStart(2, "0")}_${spinToken}`,
      type: bet.type,
      selection: bet.selection,
      amount: bet.amount,
      won: null,
      payout: null,
    })),
    betCount: draftBets.length,
    totalStake,
    result: null,
    resultColour: null,
    totalPayout: null,
    netResult: null,
    outcome: null,
    createdAt,
    lockedAt: createdAt,
    spinningAt: null,
    settledAt: null,
  };
}

function getPreviewResultColour(result) {
  const pocket = String(result);

  if (pocket === "0" || pocket === "00") {
    return "green";
  }

  const redNumbers = new Set([
    "1", "3", "5", "7", "9", "12", "14", "16", "18",
    "19", "21", "23", "25", "27", "30", "32", "34", "36",
  ]);

  return redNumbers.has(pocket) ? "red" : "black";
}

function previewRouletteBetWins(bet, result) {
  const pocket = String(result);
  const numericPocket = Number(pocket);

  if (bet.type === "straight") return bet.selection === pocket;
  if (bet.type === "colour") return getPreviewResultColour(pocket) === bet.selection;
  if (bet.type === "trio" || bet.type === "corner") return String(bet.selection).split("_").includes(pocket);

  if (pocket === "0" || pocket === "00") return false;

  if (bet.type === "dozen") {
    if (bet.selection === "1st_12") return numericPocket >= 1 && numericPocket <= 12;
    if (bet.selection === "2nd_12") return numericPocket >= 13 && numericPocket <= 24;
    if (bet.selection === "3rd_12") return numericPocket >= 25 && numericPocket <= 36;
  }

  if (bet.type === "column") {
    if (bet.selection === "column_1") return numericPocket % 3 === 1;
    if (bet.selection === "column_2") return numericPocket % 3 === 2;
    if (bet.selection === "column_3") return numericPocket % 3 === 0;
  }

  if (bet.type === "range") {
    if (bet.selection === "1-18") return numericPocket >= 1 && numericPocket <= 18;
    if (bet.selection === "19-36") return numericPocket >= 19 && numericPocket <= 36;
  }

  if (bet.type === "parity") {
    if (bet.selection === "even") return numericPocket % 2 === 0;
    if (bet.selection === "odd") return numericPocket % 2 === 1;
  }

  return false;
}

function getPreviewRoulettePayout(type, amount) {
  if (type === "straight") return amount * 38;
  if (type === "trio") return (amount / 3) * 38;
  if (type === "corner") return (amount / 2) * 19;
  if (type === "dozen" || type === "column") return amount * 3;
  if (type === "colour" || type === "range" || type === "parity") return amount * 2;
  return 0;
}

function settlePreviewRouletteSpin(spin, result) {
  const settledAt = new Date().toISOString();
  const resultColour = getPreviewResultColour(result);
  const bets = (spin?.bets || []).map((bet) => {
    const won = previewRouletteBetWins(bet, result);
    const payout = won ? getPreviewRoulettePayout(bet.type, Number(bet.amount || 0)) : 0;

    return {
      ...bet,
      won,
      payout,
    };
  });
  const totalPayout = bets.reduce((sum, bet) => sum + Number(bet.payout || 0), 0);
  const totalStake = Number(spin?.totalStake || 0);
  const netResult = totalPayout - totalStake;

  return {
    ...spin,
    status: "SETTLED",
    active: false,
    bets,
    result,
    resultColour,
    totalPayout,
    netResult,
    outcome: netResult > 0 ? "WIN" : netResult === 0 ? "BREAK_EVEN" : "LOSS",
    settledAt,
  };
}

function Roulette() {
  const [phase, setPhase] = useState("LANDING");
  const [balance, setBalance] = useState(0);
  const [draftBets, setDraftBets] = useState([]);
  const [activeSpin, setActiveSpin] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [betsOpen, setBetsOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [locking, setLocking] = useState(false);
  const [casinoError, setCasinoError] = useState("");
  const [previewMode, setPreviewMode] = useState(false);
  const [latestResults, setLatestResults] = useState([]);
  const [selectedCroupierIndex, setSelectedCroupierIndex] = useState(0);
  const [rouletteSfxMuted, setRouletteSfxMuted] = useState(false);
  const [wheelTargetResult, setWheelTargetResult] = useState(null);
  const [wheelSpinNonce, setWheelSpinNonce] = useState(0);
  const [wheelBusy, setWheelBusy] = useState(false);
  const [settling, setSettling] = useState(false);
  const formattedBalance = loading ? "—" : formatPogs(balance);
  const {
    frameRef: arcadeWalletFrameRef,
    valueRef: arcadeWalletValueRef,
    scale: arcadeWalletScale,
  } = useArcadeWalletAutoScale(formattedBalance);
  const noticeTimerRef = useRef(null);
  const pendingLockedSpinStartRef = useRef(false);

  const {
    emitRouletteSfx,
    playCroupierIntro,
    playCroupierNoticeShout,
    playSettledCroupierNumberSequence,
    stopRouletteSfx,
  } = useRouletteSfx({
    muted: rouletteSfxMuted,
  });

  useEffect(() => {
    return () => {
      stopRouletteSfx();
    };
  }, [stopRouletteSfx]);

  const activeCroupier =
    CROUPIER_IMAGES[
      selectedCroupierIndex % Math.max(CROUPIER_IMAGES.length, 1)
    ];

  const activeCroupierImage = activeCroupier?.src;
  const activeCroupierName = activeCroupier?.name || "Unknown";
  const activeCroupierVoiceId = getCroupierVoiceId(activeCroupierName);

  const handleChangeCroupier = useCallback(() => {
    const croupierCount = Math.max(CROUPIER_IMAGES.length, 1);
    const nextIndex = (selectedCroupierIndex + 1) % croupierCount;
    const nextCroupier = CROUPIER_IMAGES[nextIndex];
    const nextCroupierVoiceId = getCroupierVoiceId(nextCroupier?.name);

    setSelectedCroupierIndex(nextIndex);
    playCroupierIntro(nextCroupierVoiceId);
  }, [playCroupierIntro, selectedCroupierIndex]);

  const showNotice = useCallback((message, duration = 0) => {
    if (noticeTimerRef.current) {
      window.clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }

    setNotice(message);

    if (message) {
      playCroupierNoticeShout(activeCroupierVoiceId, message);
    }

    if (duration > 0) {
      noticeTimerRef.current = window.setTimeout(() => {
        setNotice("");
        noticeTimerRef.current = null;
      }, duration);
    }
  }, [activeCroupierVoiceId, playCroupierNoticeShout]);

  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) {
        window.clearTimeout(noticeTimerRef.current);
      }
    };
  }, []);

  const restoreCasinoState = useCallback(async () => {
    setLoading(true);
    setCasinoError("");

    try {
      const latestResponse = await getRouletteLatest();
      setLatestResults(
        Array.isArray(latestResponse?.latest)
          ? latestResponse.latest.slice(0, 8).map(String)
          : []
      );
    } catch (latestError) {
      console.error("Roulette latest history restore failed:", latestError);

      if (isLocalPreviewHost()) {
        setLatestResults(buildPreviewLatest());
      } else {
        setLatestResults([]);
      }
    }

    try {
      const [profile, active] = await Promise.all([
        getCasinoProfile(),
        getActiveRouletteSpin(),
      ]);

      setPreviewMode(false);
      setBalance(Number(profile?.wallet?.pogs || 0));

      if (active?.active && active?.spin) {
        setActiveSpin(active.spin);
        setDraftBets([]);
      } else {
        setActiveSpin(null);
      }
    } catch (error) {
      if (isLocalPreviewHost()) {
        console.warn(
          "Roulette APIs unavailable on localhost. Entering isolated development preview mode.",
          error
        );

        setPreviewMode(true);
        setBalance(DEV_PREVIEW_BALANCE);
        setActiveSpin(null);
        setDraftBets([]);
        setSelectedRegion(null);
        setCasinoError("");
      } else {
        console.error("Roulette state restore failed:", error);
        setPreviewMode(false);
        setCasinoError(
          error instanceof CasinoApiError
            ? error.message
            : "CASINO STATE UNAVAILABLE"
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    restoreCasinoState();
  }, [restoreCasinoState]);

  useEffect(() => {
    if (activeSpin?.result) {
      setWheelTargetResult(String(activeSpin.result));
    }
  }, [activeSpin?.result]);

  const describeSettledOutcome = useCallback((spin) => {
    const netResult = Number(spin?.netResult || 0);

    if (netResult > 0) {
      return `YOU WON ${formatPogs(netResult)} POGS`;
    }

    if (netResult === 0) {
      return "BREAK EVEN";
    }

    return `YOU LOST ${formatPogs(Math.abs(netResult))} POGS`;
  }, []);

  const showSettledOutcomeAfterCroupierVoice = useCallback(
    async (spin, result, croupierVoiceId) => {
      await playSettledCroupierNumberSequence({
        croupierId: croupierVoiceId,
        result: String(result),
      });

      showNotice(describeSettledOutcome(spin));
    },
    [describeSettledOutcome, playSettledCroupierNumberSequence, showNotice]
  );

  const handleWheelSpinComplete = useCallback(async (result) => {
    const croupierVoiceIdAtSettlement = activeCroupierVoiceId;

    if (!activeSpin || activeSpin.status !== "SPINNING") {
      setWheelBusy(false);
      return;
    }

    if (previewMode || String(activeSpin.spinId || "").startsWith("preview_")) {
      const settledSpin = settlePreviewRouletteSpin(activeSpin, String(result));
      setBalance((current) => current + Number(settledSpin.totalPayout || 0));
      setLatestResults((current) => [String(result), ...current].slice(0, 8));
      setActiveSpin(null);
      setWheelTargetResult(String(result));
      await showSettledOutcomeAfterCroupierVoice(
        settledSpin,
        result,
        croupierVoiceIdAtSettlement
      );
      setWheelBusy(false);
      return;
    }

    setSettling(true);

    try {
      const response = await settleRouletteSpin(activeSpin.spinId);
      const settledSpin = response.spin;

      setBalance(Number(response.balance || 0));
      setLatestResults(
        Array.isArray(response.latest)
          ? response.latest.slice(0, 8).map(String)
          : (current) => [String(result), ...current].slice(0, 8)
      );
      setActiveSpin(null);
      setWheelTargetResult(String(result));
      await showSettledOutcomeAfterCroupierVoice(
        settledSpin,
        result,
        croupierVoiceIdAtSettlement
      );
    } catch (error) {
      console.error("Roulette settlement failed:", error);

      if (
        error instanceof CasinoApiError &&
        error.code === "SPIN_ALREADY_SETTLED"
      ) {
        showNotice("SPIN ALREADY SETTLED");
        await restoreCasinoState();
      } else {
        showNotice(
          error instanceof CasinoApiError
            ? String(error.message || "ROULETTE SETTLEMENT FAILED").toUpperCase()
            : "ROULETTE SETTLEMENT FAILED"
        );
      }
    } finally {
      setSettling(false);
      setWheelBusy(false);
    }
  }, [
    activeCroupierVoiceId,
    activeSpin,
    previewMode,
    restoreCasinoState,
    showNotice,
    showSettledOutcomeAfterCroupierVoice,
  ]);

  const draftTotalStake = useMemo(
    () => draftBets.reduce((total, bet) => total + Number(bet.amount || 0), 0),
    [draftBets]
  );

  const displayedBets = activeSpin?.bets || draftBets;
  const displayedStake = activeSpin?.totalStake ?? draftTotalStake;
  const displayedStatus = activeSpin?.status || "DRAFT";
  const availableBalance = activeSpin
    ? balance
    : Math.max(0, balance - draftTotalStake);
  const betsLocked = Boolean(activeSpin);
  const hideDesktopCroupierImage =
    notice === "INSUFFICIENT FUNDS";

  const handleOpenBets = () => {
    setSelectedRegion(null);
    setBetsOpen(true);
  };

  const handleSelectRegion = (region) => {
    if (betsLocked) {
      return;
    }

    setSelectedRegion(region);
  };

  const handleAmountConfirm = (rawAmount) => {
    const amount = Number(rawAmount);

    if (!selectedRegion) {
      showNotice("SELECT A BET");
      return;
    }

    if (!Number.isSafeInteger(amount) || amount <= 0) {
      showNotice("ENTER A VALID POG AMOUNT");
      return;
    }

    if (amount > availableBalance) {
      setSelectedRegion(null);
      showNotice("INSUFFICIENT FUNDS");
      return;
    }

    const draftBetId =
      typeof window.crypto?.randomUUID === "function"
        ? `draft_${window.crypto.randomUUID()}`
        : `draft_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    setDraftBets((current) => [
      ...current,
      {
        draftBetId,
        type: selectedRegion.type,
        selection: selectedRegion.selection,
        amount,
      },
    ]);

    setSelectedRegion(null);
  };

  const handleClearDrafts = () => {
    if (betsLocked || locking || draftBets.length === 0) {
      return;
    }

    setDraftBets([]);
    setSelectedRegion(null);
  };

  const handleLock = async () => {
    if (betsLocked || locking) {
      return;
    }

    if (draftBets.length === 0) {
      showNotice("BET BEFORE LOCKING");
      return;
    }

    if (previewMode) {
      if (draftTotalStake > balance) {
        showNotice("INSUFFICIENT FUNDS");
        return;
      }

      const previewSpin = createPreviewSpin(draftBets, draftTotalStake);

      setBalance((current) => current - draftTotalStake);
      setActiveSpin(previewSpin);
      setDraftBets([]);
      setSelectedRegion(null);
      return;
    }

    setLocking(true);

    try {
      const payload = draftBets.map(({ type, selection, amount }) => ({
        type,
        selection,
        amount,
      }));

      const response = await lockRouletteBets(payload);

      setActiveSpin(response.spin);
      setBalance(Number(response.balance || 0));
      setDraftBets([]);
      setSelectedRegion(null);
    } catch (error) {
      console.error("Roulette lock failed:", error);

      if (error instanceof CasinoApiError && error.code === "INSUFFICIENT_FUNDS") {
        showNotice("INSUFFICIENT FUNDS");
      } else if (
        error instanceof CasinoApiError &&
        error.code === "ACTIVE_SPIN_EXISTS"
      ) {
        showNotice("ACTIVE SPIN EXISTS");
        await restoreCasinoState();
      } else {
        showNotice(
          error instanceof CasinoApiError
            ? String(error.message || "ROULETTE LOCK FAILED").toUpperCase()
            : "ROULETTE LOCK FAILED"
        );
      }
    } finally {
      setLocking(false);
    }
  };

  const startLockedSpinAfterNoticeClose = useCallback(async () => {
    if (wheelBusy || settling) {
      showNotice("SPIN IN PROGRESS");
      return;
    }

    if (!activeSpin || activeSpin.status !== "LOCKED") {
      showNotice("ROULETTE STATE UNAVAILABLE");
      return;
    }

    setWheelBusy(true);

    if (previewMode || String(activeSpin.spinId || "").startsWith("preview_")) {
      const result = ROULETTE_PREVIEW_POCKETS[
        Math.floor(Math.random() * ROULETTE_PREVIEW_POCKETS.length)
      ];
      const spinningAt = new Date().toISOString();

      setActiveSpin((current) => ({
        ...current,
        status: "SPINNING",
        result,
        resultColour: getPreviewResultColour(result),
        spinningAt,
      }));
      setWheelTargetResult(String(result));
      setWheelSpinNonce((current) => current + 1);
      return;
    }

    try {
      const response = await startRouletteSpin();
      const result = response?.spin?.result;

      if (!result) {
        throw new Error("Roulette spin response did not include a result");
      }

      setActiveSpin(response.spin);
      setWheelTargetResult(String(result));
      setWheelSpinNonce((current) => current + 1);
    } catch (error) {
      console.error("Roulette spin failed:", error);
      setWheelBusy(false);

      showNotice(
        error instanceof CasinoApiError
          ? String(error.message || "ROULETTE SPIN FAILED").toUpperCase()
          : "ROULETTE SPIN FAILED"
      );
    }
  }, [activeSpin, previewMode, settling, showNotice, wheelBusy]);

  const handleNoticeClose = useCallback(() => {
    const shouldStartLockedSpin =
      pendingLockedSpinStartRef.current && notice === "ALL BETS LOCKED";

    pendingLockedSpinStartRef.current = false;
    showNotice("");

    if (shouldStartLockedSpin) {
      window.setTimeout(() => {
        startLockedSpinAfterNoticeClose();
      }, 0);
    }
  }, [notice, showNotice, startLockedSpinAfterNoticeClose]);

  const renderArcadeHeader = () => (
    <div className="roulette-landing-topbar pogforts-arcade-topbar">
      <div>
        <p className="roulette-landing-kicker">POGFORTS ARCADE</p>
        <h1 className="roulette-landing-title">ROULETTE</h1>
        <p className="roulette-landing-subtitle">HIGH ROLLERS CLUB</p>
      </div>

      <div className="roulette-landing-wallet pogforts-arcade-wallet">
        <span>{previewMode ? "PREVIEW POG BALANCE" : "POG BALANCE"}</span>
        <strong ref={arcadeWalletFrameRef}>
          <span
            ref={arcadeWalletValueRef}
            className="pogforts-arcade-wallet-value"
            style={{ "--pog-wallet-scale": arcadeWalletScale }}
          >
            {!loading && Number(balance) !== 0 && (
              <img
                src={pogImage}
                alt=""
                aria-hidden="true"
                draggable="false"
              />
            )}
            {formattedBalance}
          </span>
        </strong>
      </div>
    </div>
  );

  const handleSpin = async () => {
    if (wheelBusy || settling) {
      showNotice("SPIN IN PROGRESS");
      return;
    }

    if (!activeSpin) {
      if (draftBets.length > 0) {
        showNotice("LOCK IN YOUR BETS");
      } else {
        showNotice("BET BEFORE SPINNING");
      }

      return;
    }

    if (activeSpin.status === "LOCKED") {
      pendingLockedSpinStartRef.current = true;
      showNotice("ALL BETS LOCKED");
      return;
    }

    if (activeSpin.status === "SPINNING") {
      if (activeSpin.result) {
        setWheelBusy(true);
        setWheelTargetResult(String(activeSpin.result));
        setWheelSpinNonce((current) => current + 1);
      } else {
        showNotice("SPIN IN PROGRESS");
      }

      return;
    }

    showNotice("ROULETTE STATE UNAVAILABLE");
  };

  if (phase === "LANDING") {
    return (
      <div className="roulette-landing-shell">
        <Helmet>
          <title>Roulette - Pogforts</title>
          <meta
            name="description"
            content="Enter the Pogforts Arcade and play Roulette using Pogs. Place your wagers, lock your bets and spin the wheel."
          />
        </Helmet>

        <section className="roulette-landing-panel">
          {renderArcadeHeader()}

          <div className="roulette-landing-stage">
            <div className="roulette-landing-shade" />

            <div className="roulette-landing-content">
              <img
                className="roulette-landing-logo"
                src={rouletteLogo}
                alt=""
                aria-hidden="true"
                draggable="false"
              />
              <h2>PLACE. LOCK. SPIN.</h2>
              <p className="roulette-landing-copy">
                Place Pog wagers across the table, lock your bets, spin the wheel, and win big.
              </p>

              <div className="roulette-landing-flow">
                <div>
                  <span>01</span>
                  <strong>PLACE</strong>
                  <small>CHOOSE YOUR BETS AND STAKES</small>
                </div>
                <div>
                  <span>02</span>
                  <strong>LOCK</strong>
                  <small>COMMIT YOUR WAGERS</small>
                </div>
                <div>
                  <span>03</span>
                  <strong>SPIN</strong>
                  <small>LET THE WHEEL DECIDE</small>
                </div>
              </div>

              {previewMode && (
                <p className="roulette-landing-preview-note">
                  DEVELOPMENT PREVIEW — MOCK {formatPogs(DEV_PREVIEW_BALANCE)} POG WALLET — NO DATABASE
                </p>
              )}

              {casinoError && (
                <p className="roulette-landing-error" role="alert">
                  {casinoError}
                </p>
              )}

              <button
                type="button"
                className="roulette-landing-enter-button"
                onClick={() => setPhase("CASINO")}
                disabled={loading && !previewMode}
              >
                ENTER ROULETTE
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Roulette - Pogforts</title>
        <meta
          name="description"
          content="Play Pogforts Roulette using Pogs. Place bets, lock wagers and spin the wheel in the Pogforts Casino."
        />
      </Helmet>

      <div className="casino-shell">
      <section className="casino-panel roulette-landing-panel roulette-live-panel">
        {renderArcadeHeader()}

        {previewMode && (
          <p className="roulette-readonly-message roulette-live-preview-note">
            DEVELOPMENT PREVIEW — MOCK 50,000 POG WALLET — NO DATABASE
          </p>
        )}

        <div className="roulette-live-toolbar">
          <button
            type="button"
            className="casino-change-croupier-button roulette-live-croupier-button"
            onClick={handleChangeCroupier}
          >
            CROUPIER: {activeCroupierName}
          </button>
        </div>

        <div className="casino-game-stage roulette-live-game-stage">
          <div className="casino-scene-mount" aria-label="Roulette 3D scene mount">
            <RouletteWheel3D
              status={wheelBusy ? "SPINNING" : displayedStatus}
              result={wheelTargetResult || activeSpin?.result || null}
              spinNonce={wheelSpinNonce}
              onSpinComplete={handleWheelSpinComplete}
              onRouletteSfx={emitRouletteSfx}
              rouletteSfxMuted={rouletteSfxMuted}
              onRouletteSfxMuteToggle={() =>
                setRouletteSfxMuted((current) => !current)
              }
            />
          </div>

          <div className="casino-stage-readout">
            <div>
              <span>STATE</span>
              <strong>{displayedStatus}</strong>
            </div>
            <div>
              <span>WAGERS</span>
              <strong>{displayedBets.length}</strong>
            </div>
            <div>
              <span>STAKE</span>
              <strong className="casino-stage-stake-value">
                {Number(displayedStake) !== 0 && (
                  <img
                    className="casino-stage-stake-pog-icon"
                    src={pogImage}
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                  />
                )}
                {formatPogs(displayedStake)}
              </strong>
            </div>
          </div>

          {casinoError && <p className="casino-inline-error">{casinoError}</p>}

          <div className="casino-main-actions">
            <button
              type="button"
              className="casino-button casino-main-button"
              onClick={handleOpenBets}
              disabled={loading || wheelBusy || settling}
            >
              BETS
            </button>
            <button
              type="button"
              className="casino-button casino-main-button"
              onClick={handleSpin}
              disabled={loading || wheelBusy || settling}
            >
              SPIN
            </button>
          </div>
        </div>
      </section>

      {betsOpen && (
        <RouletteBetModal
          bets={displayedBets}
          balance={balance}
          draftTotalStake={displayedStake}
          availableBalance={availableBalance}
          readOnly={betsLocked}
          status={displayedStatus}
          selectedRegion={selectedRegion}
          locking={locking}
          latestResults={latestResults}
          croupierImage={activeCroupierImage}
          hideDesktopCroupierImage={hideDesktopCroupierImage}
          onSelectRegion={handleSelectRegion}
          onAmountConfirm={handleAmountConfirm}
          onAmountClose={() => setSelectedRegion(null)}
          onLock={handleLock}
          onClearDrafts={handleClearDrafts}
          onBack={() => {
            setSelectedRegion(null);
            setBetsOpen(false);
          }}
        />
      )}

        <CasinoNoticeModal
          message={notice}
          croupierImage={activeCroupierImage}
          actionLabel={notice === "ALL BETS LOCKED" ? "SPIN" : "CLOSE"}
          onClose={handleNoticeClose}
        />
      </div>
    </>
  );
}

export default Roulette;
